import { useState } from 'react';

import { Button, Stack, Text } from '@mantine/core';

import type { RegistryOrderMessage } from '@/types/registry-documents';
import { formatRegistryMessageTime } from './RegistryOrderMessages';
import classes from './QuotedMessage.module.css';

/** How many lines of the desk's message are shown before "Show more". */
export const QUOTE_PREVIEW_LINES = 6;

interface QuotedMessageProps {
  /** The sender, when it was received, and the text: an order's message
   * or an email that reached no order alike. */
  message: Pick<RegistryOrderMessage, 'sender' | 'receivedAt' | 'text'>;
  /** The facts line when neither a sender nor a time is known. */
  fallbackFacts?: string;
  'data-testid'?: string;
}

/**
 * An email from the research desk as a quotation: who wrote and when, then
 * the text cut to a few lines with a way to see the rest. The text is the
 * sender's own: rendered as text only, never as markup, and a link in it
 * is never made clickable.
 *
 * Shared by the reply dialog (the message being answered) and the list of
 * mail that reached no order (the excerpt a row opens).
 */
export function QuotedMessage({
  message,
  fallbackFacts = 'The research desk wrote',
  'data-testid': testId,
}: Readonly<QuotedMessageProps>) {
  const [expanded, setExpanded] = useState(false);
  const lines = message.text.split('\n');
  const long = lines.length > QUOTE_PREVIEW_LINES;
  const shown = long && !expanded ? lines.slice(0, QUOTE_PREVIEW_LINES).join('\n') : message.text;
  const facts = [message.sender?.trim(), formatRegistryMessageTime(message.receivedAt)]
    .filter(Boolean)
    .join(' · ');

  return (
    <Stack gap={4} data-testid={testId}>
      <Text size="xs" c="dimmed" className={classes.facts}>
        {facts || fallbackFacts}
      </Text>
      {message.text.trim() === '' ? (
        <Text size="sm" c="dimmed">
          (no text kept)
        </Text>
      ) : (
        <Text size="sm" className={classes.quoted}>
          {shown}
        </Text>
      )}
      {long && (
        <Button
          size="compact-xs"
          variant="subtle"
          className={classes.more}
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Stack>
  );
}
